import React, { useState, useEffect, useRef } from 'react';
import { format, parseISO } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import {
  CheckCircle2,
  AlertCircle,
  Loader2,
  Calendar,
  Building2,
  MapPin,
  ChevronDown,
  ChevronUp,
  Users,
} from 'lucide-react';
import { Resource, Booking, Project } from '@/types';
import { UnallocatedBooking } from '@/hooks/useUnallocatedBookings';
import { computeRecommendations } from '@/lib/recommendationEngine';
import { ResourceMatchResult } from '@/types/recommendation';
import { useSignalWeights } from '@/hooks/useSignalWeights';
import { ResourceIdentityBlock } from '@/components/booking/ResourceIdentityBlock';
import { MatchScoreBadge } from '@/components/booking/MatchScoreBadge';
import { MatchScoreBar } from '@/components/booking/MatchScoreBar';
import { AvailabilityWindow } from '@/components/booking/AvailabilityWindow';
import { Progress } from '@/components/ui/progress';
import { useRagThresholds, getRagAccent } from '@/hooks/useRagThresholds';
import { MatchSignal } from '@/types/recommendation';
import { cn } from '@/lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

interface BatchSuggestion {
  booking: UnallocatedBooking;
  allMatches: ResourceMatchResult[];
  selectedMatch: ResourceMatchResult | null;
  isLoading: boolean;
  selected: boolean;
  pickerOpen: boolean;
  scoreDetailOpen: boolean;
}

interface AIBatchSuggestModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bookings: UnallocatedBooking[];
  resources: Resource[];
  existingBookings: Booking[];
  projects: Project[];
  onConfirm: (assignments: { bookingId: string; resourceId: string }[]) => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDateRange(startTime: string, endTime: string): string {
  const start = parseISO(startTime);
  const endRaw = parseISO(endTime);
  const endDisplay = new Date(endRaw);
  endDisplay.setDate(endDisplay.getDate() - 1);
  if (
    start.getFullYear() === endDisplay.getFullYear() &&
    start.getMonth() === endDisplay.getMonth() &&
    start.getDate() === endDisplay.getDate()
  ) {
    return format(start, 'd MMM yyyy');
  }
  return `${format(start, 'd MMM')} – ${format(endDisplay, 'd MMM yyyy')}`;
}

// ─── Score detail breakdown (mirrors ResourceRecommendationCard) ──────────────

function SignalDetailRow({ signal }: { signal: MatchSignal }) {
  const thresholds = useRagThresholds();
  const hasNoData = signal.displayScore === null;
  const shownValue = hasNoData ? null : (signal.displayScore ?? signal.score);
  const barValue = shownValue ?? 0;
  const accent = getRagAccent(barValue, thresholds);
  const scoreColour = hasNoData
    ? 'text-muted-foreground'
    : accent === 'success'
      ? 'text-emerald-600'
      : accent === 'warning'
      ? 'text-amber-500'
      : 'text-destructive';

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-foreground">{signal.label}</span>
        <span className={cn('text-xs font-bold tabular-nums', scoreColour)}>
          {hasNoData ? '—' : `${shownValue}%`}
        </span>
      </div>
      <Progress
        variant="mini"
        value={barValue}
        accent={hasNoData ? undefined : accent}
        aria-label={`${signal.label}: ${hasNoData ? 'N/A' : `${shownValue}%`}`}
      />
      {signal.explanation && (
        <p className="text-xs text-muted-foreground leading-snug">{signal.explanation}</p>
      )}
    </div>
  );
}

interface ScoreDetailSectionProps {
  signals: MatchSignal[];
  expanded: boolean;
  onToggle: () => void;
}

function ScoreDetailSection({ signals, expanded, onToggle }: ScoreDetailSectionProps) {
  return (
    <>
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          'w-full flex items-center justify-between px-3.5 py-2 text-xs text-muted-foreground border-t border-dashed',
          'hover:bg-muted/40 hover:text-foreground transition-colors',
          expanded && 'bg-muted/30 text-foreground'
        )}
        aria-label={expanded ? 'Hide score detail' : 'Show score detail'}
        aria-expanded={expanded}
      >
        <span className="text-[10px] font-semibold uppercase tracking-widest">
          Score detail
        </span>
        {expanded ? (
          <ChevronUp className="h-3 w-3 shrink-0 transition-transform duration-150" />
        ) : (
          <ChevronDown className="h-3 w-3 shrink-0 transition-transform duration-150" />
        )}
      </button>

      {expanded && (
        <div className="px-3.5 pb-3.5 pt-2.5 border-t grid grid-cols-2 gap-x-6 gap-y-4">
          {signals.map(s => (
            <SignalDetailRow key={s.key} signal={s} />
          ))}
        </div>
      )}
    </>
  );
}

// ─── Alternative resource picker dropdown ────────────────────────────────────

// ─── Mini score strip for alternative picker rows ────────────────────────────

const SIGNAL_SHORT_LABELS: Record<MatchSignal['key'], string> = {
  availability: 'Avail',
  role:         'Role',
  skills:       'Skills',
  utilisation:  'Util',
  location:     'Loc',
  budget:       'Budget',
};

function MiniScoreStrip({ signals }: { signals: MatchSignal[] }) {
  const thresholds = useRagThresholds();
  return (
    <div className="flex flex-wrap gap-1 mt-1.5">
      {signals.map((sig) => {
        const hasNoData = sig.displayScore === null;
        const pct = hasNoData ? 0 : Math.round(sig.displayScore ?? sig.score);
        const accent = hasNoData ? undefined : getRagAccent(pct, thresholds);
        const label = SIGNAL_SHORT_LABELS[sig.key] ?? sig.label;
        return (
          <span
            key={sig.key}
            title={sig.explanation}
            className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium border cursor-default"
            style={{
              borderColor: accent ? `hsl(var(--${accent}-border, var(--border)))` : undefined,
              color:       accent ? `hsl(var(--${accent}-foreground, var(--foreground)))` : undefined,
              background:  accent ? `hsl(var(--${accent}-subtle, var(--muted)))` : undefined,
            }}
          >
            <span className="opacity-70">{label}</span>
            <span className="font-semibold">{hasNoData ? '—' : `${pct}%`}</span>
          </span>
        );
      })}
    </div>
  );
}

interface AlternativePickerProps {
  matches: ResourceMatchResult[];
  selectedId: string;
  requestedStart: Date;
  requestedEnd: Date;
  onSelect: (match: ResourceMatchResult) => void;
  onClose: () => void;
}

function AlternativePicker({
  matches,
  selectedId,
  requestedStart,
  requestedEnd,
  onSelect,
  onClose,
}: AlternativePickerProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        // Only close if the click was not on the trigger button itself
        // (the trigger button is outside ref, but already handles toggle via onTogglePicker)
        const target = e.target as HTMLElement;
        if (!target.closest('[data-picker-trigger]')) {
          onClose();
        }
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="border-t bg-popover overflow-hidden"
    >
      {/* Header */}
      <div className="px-4 py-2.5 border-b bg-muted/40 flex items-center gap-2">
        <div className="flex h-5 w-5 items-center justify-center rounded bg-primary/10">
          <Users className="h-3 w-3 text-primary" />
        </div>
        <p className="text-xs font-medium text-foreground">
          Select a different resource
        </p>
        <span className="text-xs text-muted-foreground ml-auto">ranked by match score</span>
      </div>

      {/* Ranked list */}
      <div className="max-h-[280px] overflow-y-auto divide-y">
        {matches.map((m, idx) => {
          const isActive = m.resource.id === selectedId;
          return (
            <button
              key={m.resource.id}
              onClick={() => { onSelect(m); onClose(); }}
              className={cn(
                'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors',
                'hover:bg-muted/60',
                isActive && 'bg-primary/5'
              )}
            >
              {/* Rank */}
              <span
                className={cn(
                  'inline-flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold border shrink-0',
                  idx === 0
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-muted text-muted-foreground border-muted-foreground/20'
                )}
              >
                {idx + 1}
              </span>

              {/* Identity */}
              <div className="flex-1 min-w-0">
                <ResourceIdentityBlock resource={m.resource} size="sm" />
                <AvailabilityWindow
                  availablePeriods={m.availablePeriods}
                  availabilityStatus={m.availabilityStatus}
                  requestedStart={requestedStart}
                  requestedEnd={requestedEnd}
                  compact
                />
                <MiniScoreStrip signals={m.signals} />
              </div>

              {/* Score + selected indicator */}
              <div className="flex items-center gap-2 shrink-0">
                <MatchScoreBadge score={m.overallScore} />
                {isActive && <CheckCircle2 className="h-4 w-4 text-primary" />}
              </div>
            </button>
          );
        })}
      </div>

      <div className="px-4 py-2 border-t bg-muted/20">
        <p className="text-[10px] text-muted-foreground">
          {matches.length} resource{matches.length !== 1 ? 's' : ''} analysed
        </p>
      </div>
    </div>
  );
}

// ─── Availability pill (inline, smaller than AvailabilityWindow compact) ─────

function AvailabilityPill({ status }: { status: 'free' | 'partial' | 'conflict' }) {
  if (status === 'free')
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800 whitespace-nowrap">
        <CheckCircle2 className="h-3 w-3 shrink-0" />
        Free
      </span>
    );
  if (status === 'partial')
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800 whitespace-nowrap">
        <Loader2 className="h-3 w-3 shrink-0" />
        Partial
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium bg-red-50 text-red-700 border border-red-200 dark:bg-red-950/40 dark:text-red-400 dark:border-red-800 whitespace-nowrap">
      <AlertCircle className="h-3 w-3 shrink-0" />
      Conflict
    </span>
  );
}

// ─── Left detail panel — shown when a candidate is focused ───────────────────

// Ordinal helper — 1→"Best match", 2→"2nd match", 3→"3rd match", etc.
function ordinalMatchLabel(rank: number): string {
  if (rank === 1) return 'Best match';
  if (rank === 2) return '2nd match';
  if (rank === 3) return '3rd match';
  return `${rank}th match`;
}

interface DetailPanelProps {
  match: ResourceMatchResult;
  booking: BatchSuggestion['booking'];
  matchRank: number;          // 1-based rank of the currently selected match
  allMatchCount: number;
}

function DetailPanel({ match, booking, matchRank, allMatchCount }: DetailPanelProps) {
  const startDate = parseISO(booking.startTime);
  const endDate   = parseISO(booking.endTime);

  // Role mismatch: the role signal exists and scored below 50, and the resource
  // role doesn't equal the booking's required role.
  const roleSignal        = match.signals?.find(s => s.key === 'role');
  const hasRoleMismatch   = !!(
    booking.requiredRole &&
    match.resource.role &&
    roleSignal &&
    roleSignal.score < 50 &&
    match.resource.role.toLowerCase() !== booking.requiredRole.toLowerCase()
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Context label — "Showing score for: [booking name]" */}
      <div className="rounded-lg bg-muted/50 border px-3 py-2">
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-0.5">
          Showing score for
        </p>
        <p className="text-xs font-semibold text-foreground truncate leading-snug">
          {booking.projectName}
        </p>
        {booking.requiredRole && (
          <p className="text-[10px] text-muted-foreground mt-0.5">{booking.requiredRole}</p>
        )}
      </div>

      {/* Rank label */}
      <div className="flex items-center gap-1.5">
        <div className={cn(
          'flex h-4 w-4 items-center justify-center rounded shrink-0',
          matchRank === 1 ? 'bg-primary/10' : 'bg-muted'
        )}>
        </div>
        <span className={cn(
          'text-[10px] font-semibold uppercase tracking-wider',
          matchRank === 1 ? 'text-primary' : 'text-muted-foreground'
        )}>
          {ordinalMatchLabel(matchRank)}
        </span>
        {matchRank > 1 && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 font-normal text-amber-600 border-amber-300">
            Not top pick
          </Badge>
        )}
        <span className="ml-auto text-[10px] text-muted-foreground tabular-nums">
          {allMatchCount} resource{allMatchCount !== 1 ? 's' : ''} analysed
        </span>
      </div>

      {/* Identity */}
      <ResourceIdentityBlock resource={match.resource} size="md" />

      {/* Score + bar */}
      <div className="flex items-center gap-3">
        <MatchScoreBadge score={match.overallScore} />
        <div className="flex-1">
          <MatchScoreBar score={match.overallScore} />
        </div>
      </div>

      {/* Availability */}
      <AvailabilityWindow
        availablePeriods={match.availablePeriods}
        availabilityStatus={match.availabilityStatus}
        requestedStart={startDate}
        requestedEnd={endDate}
        compact={false}
      />

      {/* Signal breakdown */}
      {match.signals && match.signals.length > 0 && (
        <div className="border-t pt-3 grid grid-cols-2 gap-x-5 gap-y-3.5">
          <p className="col-span-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
            Score breakdown
          </p>
          {match.signals.map(s => (
            <SignalDetailRow key={s.key} signal={s} />
          ))}

          {/* Role mismatch note — shown when role score is low and resource role differs */}
          {hasRoleMismatch && (
            <div className="col-span-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2.5 dark:bg-amber-950/30 dark:border-amber-800">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <p className="text-[11px] text-amber-800 dark:text-amber-300 leading-snug">
                  <span className="font-semibold">Role mismatch — intentional recommendation.</span>{' '}
                  {match.resource.name} is a <span className="font-medium">{match.resource.role}</span>, not a{' '}
                  <span className="font-medium">{booking.requiredRole}</span>. This is the best available match given current team capacity — not an error.
                </p>
              </div>
            </div>
          )}

          {/* Low-score justification — shown when any non-role signal is 0% and no role note already shown */}
          {!hasRoleMismatch && match.signals.some(s => s.score === 0) && (
            <div className="col-span-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 dark:bg-amber-950/30 dark:border-amber-800">
              <p className="text-[11px] text-amber-800 dark:text-amber-300 leading-snug">
                <span className="font-semibold">Best available given current capacity.</span>{' '}
                Some criteria couldn't be fully matched — this resource scores highest overall across your team right now.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Single booking row ───────────────────────────────────────────────────────

interface BatchRowProps {
  s: BatchSuggestion;
  isFocused: boolean;
  onFocus: () => void;
  onToggleSelect: () => void;
  onTogglePicker: () => void;
  onClosePicker: () => void;
  onSelectAlternative: (match: ResourceMatchResult) => void;
}

function BatchRow({
  s,
  isFocused,
  onFocus,
  onToggleSelect,
  onTogglePicker,
  onClosePicker,
  onSelectAlternative,
}: BatchRowProps) {
  const dateRange       = formatDateRange(s.booking.startTime, s.booking.endTime);
  const startDate       = parseISO(s.booking.startTime);
  const endDate         = parseISO(s.booking.endTime);
  const hasAlternatives = s.allMatches.length > 1;

  const urgencyBorder =
    s.booking.urgency === 'critical' ? 'border-l-destructive' :
    s.booking.urgency === 'soon'     ? 'border-l-yellow-400'  : 'border-l-border';

  return (
    <div
      className={cn(
        'rounded-lg border border-l-[3px] bg-card transition-all duration-150 cursor-pointer overflow-hidden',
        urgencyBorder,
        isFocused  && 'border-primary/40 bg-primary/[0.02] shadow-sm ring-1 ring-primary/20',
        !isFocused && 'hover:bg-muted/30',
        !s.selectedMatch && !s.isLoading && 'opacity-60'
      )}
      onClick={onFocus}
    >
      <div className="px-4 py-3 flex items-center gap-3">

        {/* Checkbox — stop propagation so clicking it doesn't also focus */}
        <Checkbox
          checked={s.selected && !!s.selectedMatch}
          disabled={!s.selectedMatch || s.isLoading}
          onCheckedChange={onToggleSelect}
          onClick={e => e.stopPropagation()}
          className="shrink-0"
        />

        {/* Booking info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold leading-snug truncate">
              {s.booking.projectName}
            </span>
            {s.booking.requiredRole && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 font-medium shrink-0">
                {s.booking.requiredRole}
              </Badge>
            )}
          </div>
          <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3 shrink-0" />
              {dateRange}
            </span>
            {s.booking.customer && (
              <span className="flex items-center gap-1 truncate">
                <Building2 className="h-3 w-3 shrink-0" />
                {s.booking.customer}
              </span>
            )}
          </div>
        </div>

        {/* Candidate summary — the key change: one line, no breakdown */}
        <div className="shrink-0 flex items-center gap-2 min-w-0">
          {s.isLoading ? (
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Analysing…
            </span>
          ) : s.selectedMatch ? (
            <>
              {/* Rank badge — always visible, updates when user swaps */}
              {s.allMatches.length > 0 && (() => {
                const rank = s.allMatches.findIndex(m => m.resource.id === s.selectedMatch!.resource.id) + 1;
                const isTop = rank === 1;
                return (
                  <span className={cn(
                    'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap shrink-0 border',
                    isTop
                      ? 'bg-primary/10 text-primary border-primary/20'
                      : 'bg-muted text-muted-foreground border-muted-foreground/20'
                  )}>
                    {ordinalMatchLabel(rank)}
                  </span>
                );
              })()}

              {/* Resource name */}
              <span className="text-sm font-medium text-foreground whitespace-nowrap">
                {s.selectedMatch.resource.name}
              </span>

              {/* Availability pill */}
              <AvailabilityPill status={s.selectedMatch.availabilityStatus} />

              {/* Score badge */}
              <MatchScoreBadge score={s.selectedMatch.overallScore} />

              {/* Alternatives picker trigger */}
              {hasAlternatives && (
                <div onClick={e => e.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="sm"
                    data-picker-trigger
                    className="h-6 px-2 text-[11px] text-muted-foreground hover:text-foreground gap-1 whitespace-nowrap"
                    onClick={onTogglePicker}
                  >
                    {s.allMatches.length} matches
                    {s.pickerOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                  </Button>
                </div>
              )}
            </>
          ) : (
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <AlertCircle className="h-3.5 w-3.5 text-yellow-500 shrink-0" />
              No match
            </span>
          )}
        </div>
      </div>

      {/* ── Full-width alternative picker — rendered below the row, contained within card ── */}
      {s.pickerOpen && s.selectedMatch && (
        <div onClick={e => e.stopPropagation()}>
          <AlternativePicker
            matches={s.allMatches}
            selectedId={s.selectedMatch.resource.id}
            requestedStart={startDate}
            requestedEnd={endDate}
            onSelect={onSelectAlternative}
            onClose={onClosePicker}
          />
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function AIBatchSuggestModal({
  open,
  onOpenChange,
  bookings,
  resources,
  existingBookings,
  projects,
  onConfirm,
}: AIBatchSuggestModalProps) {
  const [suggestions, setSuggestions]         = useState<BatchSuggestion[]>([]);
  const [focusedBookingId, setFocusedBookingId] = useState<string | null>(null);
  const signalWeights = useSignalWeights();

  // ── Run matching engine on open ───────────────────────────────────────────
  useEffect(() => {
    if (!open) return;

    setFocusedBookingId(null);
    setSuggestions(
      bookings.map(b => ({
        booking: b,
        allMatches: [],
        selectedMatch: null,
        isLoading: true,
        selected: true,
        pickerOpen: false,
        scoreDetailOpen: false,
      }))
    );

    const timers: ReturnType<typeof setTimeout>[] = [];

    bookings.forEach((booking, idx) => {
      const timer = setTimeout(() => {
        const startDate = parseISO(booking.startTime);
        const endDate = parseISO(booking.endTime);

        const output = computeRecommendations({
          startDate,
          endDate,
          requiredRole: booking.requiredRole,
          location: booking.location,
          requiredSkills: booking.requiredSkills ?? [],
          projectId: booking.projectId,
          projects,
          allResources: resources.filter(r => r.id !== 'unallocated'),
          existingBookings,
          signalWeights,
        });

        const allMatches: ResourceMatchResult[] = [
          ...output.cleanMatches,
          ...output.conflictMatches,
        ];

        setSuggestions(prev => {
          const next = prev.map(s =>
            s.booking.id === booking.id
              ? {
                  ...s,
                  allMatches,
                  selectedMatch: allMatches[0] ?? null,
                  isLoading: false,
                  selected: allMatches.length > 0,
                }
              : s
          );
          // Once the last booking loads, default-focus the second row as a
          // teaser so the detail panel is never completely empty on small batches.
          // (The first row is implicitly described by the "Showing score for" label
          // at the top of the panel; the second gives the planner a comparison point.)
          const allDone = next.every(s => !s.isLoading);
          if (allDone) {
            const teaserBooking = next[1] ?? next[0];
            if (teaserBooking) {
              setFocusedBookingId(id => id ?? teaserBooking.booking.id);
            }
          }
          return next;
        });
      }, idx * 150);

      timers.push(timer);
    });

    return () => timers.forEach(clearTimeout);
  }, [open, bookings, resources, existingBookings]);

  // ── Mutations ──────────────────────────────────────────────────────────────

  const toggleSelect = (bookingId: string) =>
    setSuggestions(prev =>
      prev.map(s =>
        s.booking.id === bookingId && s.selectedMatch
          ? { ...s, selected: !s.selected }
          : s
      )
    );

  const togglePicker = (bookingId: string) =>
    setSuggestions(prev =>
      prev.map(s =>
        s.booking.id === bookingId
          ? { ...s, pickerOpen: !s.pickerOpen }
          : { ...s, pickerOpen: false }
      )
    );

  const closePicker = (bookingId: string) =>
    setSuggestions(prev =>
      prev.map(s => (s.booking.id === bookingId ? { ...s, pickerOpen: false } : s))
    );

  const selectAlternative = (bookingId: string, match: ResourceMatchResult) =>
    setSuggestions(prev =>
      prev.map(s =>
        s.booking.id === bookingId
          ? { ...s, selectedMatch: match, selected: true, pickerOpen: false }
          : s
      )
    );

  // ── Derived ────────────────────────────────────────────────────────────────

  const isAnyLoading     = suggestions.some(s => s.isLoading);
  const selectedCount    = suggestions.filter(s => s.selected && s.selectedMatch).length;
  const needsReviewCount = suggestions.filter(s => !s.isLoading && s.allMatches.length === 0).length;

  const focusedSuggestion = focusedBookingId
    ? suggestions.find(s => s.booking.id === focusedBookingId) ?? null
    : null;

  const handleConfirm = () => {
    const assignments = suggestions
      .filter(s => s.selected && s.selectedMatch)
      .map(s => ({ bookingId: s.booking.id, resourceId: s.selectedMatch!.resource.id }));
    onConfirm(assignments);
    onOpenChange(false);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[1220px] w-[95vw] flex flex-col h-auto max-h-[90vh] p-0 gap-0 overflow-hidden"
        onInteractOutside={e => e.preventDefault()}
        onPointerDownOutside={e => e.preventDefault()}
      >
        {/* ── Header — mirrors ResourceRecommendationDialog exactly ── */}
        <DialogHeader className="px-5 pt-5 pb-3 shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 shrink-0">
              </div>
              <div>
                <DialogTitle className="text-sm font-semibold leading-tight">
                  Review Suggested Resources
                </DialogTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {bookings.length} booking{bookings.length !== 1 ? 's' : ''} ready to confirm
                </p>
              </div>
            </div>

            {/* Live assignment count — same pattern as "X selected" badge in booking form */}
            {selectedCount > 0 && (
              <Badge variant="secondary" className="text-xs shrink-0 mt-0.5">
                {selectedCount} ready to assign
              </Badge>
            )}
          </div>

          {/* Summary chips — shown once loading finishes */}
          {!isAnyLoading && (
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <Badge variant="secondary" className="text-xs gap-1.5">
                <CheckCircle2 className="h-3 w-3 text-green-500" />
                {selectedCount} matched
              </Badge>
              {needsReviewCount > 0 && (
                <Badge variant="outline" className="text-xs gap-1.5 text-muted-foreground">
                  <AlertCircle className="h-3 w-3 text-yellow-500" />
                  {needsReviewCount} need manual review
                </Badge>
              )}
              <p className="text-xs text-muted-foreground ml-1">
                Click any row to review the score breakdown. Use <span className="font-medium text-foreground">{suggestions[0]?.allMatches.length ?? 15} matches ∨</span> to swap to a ranked alternative
              </p>
            </div>
          )}
        </DialogHeader>

        <div className="border-b shrink-0" />

        {/* ── Master-detail body ── */}
        <div className="flex min-h-0 overflow-hidden" style={{ maxHeight: 'min(600px, 68vh)' }}>

          {/* ── LEFT: Booking list ── */}
          <ScrollArea className="flex-1 min-w-0 min-h-0">
            <ScrollAreaViewport className="h-full">
              <div className="px-4 py-3 space-y-1.5">
                {suggestions.map(s => (
                  <BatchRow
                    key={s.booking.id}
                    s={s}
                    isFocused={focusedBookingId === s.booking.id}
                    onFocus={() => setFocusedBookingId(s.booking.id)}
                    onToggleSelect={() => toggleSelect(s.booking.id)}
                    onTogglePicker={() => togglePicker(s.booking.id)}
                    onClosePicker={() => closePicker(s.booking.id)}
                    onSelectAlternative={match => selectAlternative(s.booking.id, match)}
                  />
                ))}
              </div>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>

          {/* ── RIGHT: Detail panel ── */}
          <div className="w-[380px] shrink-0 border-l overflow-y-auto p-5 bg-muted/20">
            {focusedSuggestion?.selectedMatch ? (
              <DetailPanel
                match={focusedSuggestion.selectedMatch}
                booking={focusedSuggestion.booking}
                matchRank={
                  focusedSuggestion.allMatches.findIndex(
                    m => m.resource.id === focusedSuggestion.selectedMatch!.resource.id
                  ) + 1 || 1
                }
                allMatchCount={focusedSuggestion.allMatches.length}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center gap-3 py-16">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-muted">

                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">No booking selected</p>
                  <p className="text-xs text-muted-foreground mt-1 leading-snug max-w-[180px] mx-auto">
                    Click any booking row to see the full candidate score breakdown
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Footer — mirrors ResourceRecommendationDialog footer exactly ── */}
        <div className="border-t shrink-0 px-5 py-3 flex items-center justify-between bg-muted/30">
          <p className="text-xs text-muted-foreground">
            {isAnyLoading ? (
              <span className="flex items-center gap-1.5">
                <Loader2 className="h-3 w-3 animate-spin" />
                Computing matches…
              </span>
            ) : selectedCount === 0 ? (
              'No bookings selected'
            ) : (
              <>
                <span className="font-medium text-foreground">{selectedCount}</span> of{' '}
                {bookings.length} booking{bookings.length !== 1 ? 's' : ''} will be assigned
              </>
            )}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              variant="default"
              size="sm"
              disabled={selectedCount === 0 || isAnyLoading}
              onClick={handleConfirm}
              className="gap-1.5"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              Confirm {selectedCount > 0 ? `${selectedCount} assignment${selectedCount !== 1 ? 's' : ''}` : 'assignments'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
